import React, { useState, useEffect } from 'react'
import { IoIosCloseCircleOutline } from 'react-icons/io'
import { ErrorNotification, SuccessNotification } from '../../utils'
import api from '../../utils/api'
import useSWR from 'swr'
import { fetcher } from '../../utils/api'

const EidtInviteModal = ({ onClose, selectedTeam, onConfirm }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [selectedRoleIds, setSelectedRoleIds] = useState([])
  const [loadingUserDetails, setLoadingUserDetails] = useState(false)

  const { data: availableRoles, isLoading: rolesLoading } = useSWR(
    'roles/',
    fetcher
  )
  const roles = Array.isArray(availableRoles) ? availableRoles : availableRoles?.results || []

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!selectedTeam?.id || !roles.length) return

      try {
        setLoadingUserDetails(true)
        const response = await api.get(`managed-users/${selectedTeam.id}`)

        if (Array.isArray(response.data?.assigned_roles)) {
          const initialRoleIds = response.data.assigned_roles
            .map((existingRole) => existingRole.role_id || existingRole.id)
            .filter(Boolean)
          setSelectedRoleIds(initialRoleIds)
        }
      } catch (error) {
        ErrorNotification(error.response?.data || error.message || 'Failed to fetch user details')
      } finally {
        setLoadingUserDetails(false)
      }
    }

    if (selectedTeam?.id && roles.length) {
      fetchUserDetails()
    }
  }, [selectedTeam?.id, roles.length])


  const handleRoleToggle = (roleId) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    )
  }

  const handleUpdate = async () => {
    if (!selectedTeam?.id) {
      ErrorNotification("Invalid team member selected")
      return
    }

    if (selectedRoleIds.length === 0) {
      ErrorNotification("Please select at least one role.")
      return
    }

    setIsLoading(true)
    try {
      const response = await api.patch(`managed-users/${selectedTeam.id}/update_permissions/`, {
        role_ids: selectedRoleIds
      })

      SuccessNotification(response.data.message || "User permissions updated successfully!")
      onConfirm?.()
      onClose()
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.role_ids && Array.isArray(error.response.data.role_ids)) {
        ErrorNotification(error.response.data.role_ids[0])
      } else if (error.response?.data?.message) {
        ErrorNotification(error.response.data.message)
      } else {
        ErrorNotification("An error occurred while updating permissions.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white overflow-hidden dark:bg-dark rounded-lg shadow-lg w-full md:max-w-3xl relative max-h-[90vh] flex flex-col">
        <div className='flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 dark:border-border-dark'>
          <h2 className='text-xl font-bold dark:text-white'>
            Edit user role
          </h2>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <IoIosCloseCircleOutline className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 p-4 relative">
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-bold text-yellowcolor">
              User Role Update
            </h2>
            <p className="text-sm text-gray-500">
              Select the required role to successfully update user’s role.
            </p>
          </div>

          <div className='space-y-4'>
              <div className='w-full space-y-2'>
                <label htmlFor="email" className="text-sm font-medium dark:text-white">Email</label>
                <input
                  type="email"
                  placeholder='Email'
                  className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                value={selectedTeam?.email || ''}
                disabled
              />
            </div>

            <div className='w-full space-y-4'>
              <label className="text-sm font-medium dark:text-white">Roles & Permissions</label>
              {rolesLoading || loadingUserDetails ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-6 h-6 border-t-2 border-b-2 border-yellowcolor rounded-full animate-spin"></div>
                  <span className="ml-2 text-gray-500">Loading roles...</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {roles?.map((role) => {
                    const isSelected = selectedRoleIds.includes(role.id)

                    return (
                      <div key={role.id} className="border dark:border-border-dark border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id={`role-${role.id}`}
                              checked={isSelected}
                              onChange={() => handleRoleToggle(role.id)}
                              className="w-4 h-4 text-yellowcolor bg-gray-100 border-gray-300 rounded focus:ring-yellowcolor dark:focus:ring-yellowcolor dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <label htmlFor={`role-${role.id}`} className="text-sm font-medium dark:text-white cursor-pointer">
                              {role.name}
                            </label>
                          </div>
                        </div>

                        <p className="text-xs text-gray-500 ml-7">{role.description || 'No description'}</p>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>


        </div>

        <div className='border-t p-4 z-50 border-gray-200 flex flex-row items-end justify-end w-full bg-white dark:bg-dark flex-shrink-0 dark:border-border-dark'>
          <div className="flex flex-row items-end justify-end gap-4">
            <button
              onClick={onClose}
              className='px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg'>
              Close
            </button>

            <button
              disabled={isLoading}
              onClick={handleUpdate}
              className={`px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}>
              {isLoading ? <div className='flex flex-row items-center gap-2'><div className='w-4 h-4 border-t-2 border-b-2 border-white rounded-full animate-spin'></div> Updating...</div> : "Update User Role"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EidtInviteModal